/** @format */

import { GTMPDPRoomGalleryImageAndAllClick, GTMvirtualTour } from '@/ducks/googleTagManager/helpers/google-tag-manager';
import { TRoom } from '@/ducks/room/declarations';
import { utilityFormatPhoto } from '@/ducks/shared/helpers/getRoomPictures';
import { useAppSelector } from '@/redux/hooks';
import { utilityGetPrice } from '@/utils/helpers/utilityGetPrice';
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import 'swiper/css';
import { selectorBookingCommercialConditions } from '@/@ducks/bookingNew/selectors';
import DesktopGalleryPreview from './DesktopGalleryPreview';
import GalleryModal from './GalleryModal';
import MobileGalleryPreview from './MobileGalleryPreview';
import VrModal from './VrModal';

interface RoomGalleryProps {
  photos: TRoom['media'];
  room: TRoom;
}

const RoomGallery: React.FC<RoomGalleryProps> = ({ photos, room }) => {
  const galleryRef = React.useRef<HTMLDivElement>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [showInsideGallery, setShowInsideGallery] = useState(false);
  const mediaFormatted = useMemo(() => photos.filter(e => e.type !== 'vr'), [photos]);
  const mediaFiltered = useMemo(() => photos.find(mediaType => mediaType.type === 'vr'), [photos]);
  const hasVR = useMemo(() => !!mediaFiltered, [mediaFiltered]);
  const vrMedia = useMemo(() => hasVR && photos.filter(singleMedia => singleMedia.type === 'vr')[0], [hasVR, photos]);
  const commercialConditions = useAppSelector(selectorBookingCommercialConditions);
  const [formattedPrice, setFormattedPrice] = useState<number>(0);

  const picturesForGallery = useMemo(
    () =>
      mediaFormatted
        .map((photo, index) => {
          if (photo.type === 'video') {
            return {
              index: index,
              url: photo.url,
              type: 'video',
              poster: photo.poster,
            };
          }
          if (photo.url || photo.altUrl) {
            return {
              index: index,
              url: utilityFormatPhoto(photo.altUrl || photo.url),
              alt: photo.label + ' ' + (index + 1),
              thumbnail: utilityFormatPhoto(photo.thumbnail),
              representative: photo.representative,
            };
          }
          return undefined;
        })
        .filter(Boolean),
    [photos],
  );

  const mosaicPicturesForGallery = useMemo(() => {
    const transformedArray = [];

    for (let i = 0; i < picturesForGallery.length; i += 3) {
      if (i + 1 < picturesForGallery.length) {
        transformedArray.push(
          [picturesForGallery[i], undefined],
          [picturesForGallery[i + 1], picturesForGallery[i + 2]],
        );
      } else {
        transformedArray.push([picturesForGallery[i], undefined]);
      }
    }

    return transformedArray;
  }, [picturesForGallery]);

  const firstThree = useMemo(
    () => (picturesForGallery.length > 2 ? picturesForGallery.slice(0, 3) : picturesForGallery),
    [photos],
  );

  const handleClickVrIcon = () => {
    setModalOpen(true);
    GTMvirtualTour({
      city: room.city.nameEn,
      moreInfo: room.code,
      monthlyRate: formattedPrice,
    });
  };
  const onSetModalClose = () => setModalOpen(false);

  const handleGTMPDPRoomGalleryImageAndAllClick = () => {
    if (room) {
      GTMPDPRoomGalleryImageAndAllClick(room.code, formattedPrice);
    }
  };

  useEffect(() => {
    if (room) {
      const { getFormattedPrice } = utilityGetPrice({ commercialConditions, room });
      setFormattedPrice(getFormattedPrice());
    }
  }, [room]);

  return (
    <Gallery ref={galleryRef}>
      {firstThree && (
        <DesktopGalleryPreview
          firstThree={firstThree}
          setShowInsideGallery={setShowInsideGallery}
          handleGTMPDPRoomGalleryImageAndAllClick={handleGTMPDPRoomGalleryImageAndAllClick}
          handleClickVrIcon={handleClickVrIcon}
          hasVR={Boolean(hasVR && vrMedia)}
        />
      )}
      <MobileGalleryPreview
        picturesForGallery={picturesForGallery}
        hasVR={Boolean(hasVR && vrMedia)}
        handleClickVrIcon={handleClickVrIcon}
        handleGTMPDPRoomGalleryImageAndAllClick={handleGTMPDPRoomGalleryImageAndAllClick}
        setShowInsideGallery={setShowInsideGallery}
      />
      <GalleryModal
        showInsideGallery={showInsideGallery}
        setShowInsideGallery={setShowInsideGallery}
        mosaicPicturesForGallery={mosaicPicturesForGallery}
        picturesForGallery={picturesForGallery}
        room={room}
        formattedPrice={formattedPrice}
      />
      {hasVR && vrMedia && <VrModal modalOpen={modalOpen} onSetModalClose={onSetModalClose} vrMedia={vrMedia} />}
    </Gallery>
  );
};

export default RoomGallery;

const Gallery = styled.div`
  position: relative;
  display: grid;
  justify-content: center;
  align-items: center;
  height: 350px;
  max-height: 350px;
  gap: 12px;

  grid-template-columns: repeat(8, 1fr);

  @media screen and (max-width: ${props => props.theme.breakpoints.xs}) {
    grid-template-columns: 1fr;
    grid-template-rows: 1fr;
    gap: 0;
    .img-container {
      display: none;
    }
  }
`;
